import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Plus, Briefcase, Search, RotateCcw, X,
  FolderKanban, TrendingUp, Activity, ChevronDown,
  MoreHorizontal, Eye, Pencil
} from 'lucide-react'
import { portfoliosService, Portfolio } from '../services/portfolios'
import { dashboardService } from '../services/dashboard'
import { toast } from 'sonner'
import { getErrorMessage, formatCurrency } from '../utils/apiTransformers'
import { HeroSection } from '../components'
import { useLabels } from '../contexts/LabelsContext'
import { StatusBadge } from '../components/StatusBadge'
import type { ApiErrorLike } from '../utils/apiError'

interface PortfolioWithStats extends Portfolio {
  project_count?: number
  program_count?: number
  total_budget?: number
  total_actual_cost?: number
  portfolio_code?: string
  priority?: string
  portfolio_type?: string
  manager_name?: string
}

export default function PortfolioList() {
  const navigate = useNavigate()
  const { label } = useLabels()
  const [portfolios, setPortfolios] = useState<PortfolioWithStats[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [filterType, setFilterType] = useState('all')
  const [showDeleted, setShowDeleted] = useState(false)
  const [openMenuId, setOpenMenuId] = useState<number | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [stats, setStats] = useState({
    totalPortfolios: 0,
    activePortfolios: 0,
    totalPrograms: 0,
    totalProjects: 0,
  })

  useEffect(() => {
    fetchPortfolios()
  }, [showDeleted])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const fetchPortfolios = async () => {
    setLoading(true)
    try {
      const params: Record<string, string | number | boolean> = {}
      if (showDeleted) {
        params.include_deleted = true
      }
      
      // Fetch portfolios and portfolio summary in parallel
      const [portfoliosData, portfolioSummary] = await Promise.all([
        portfoliosService.list(params),
        dashboardService.getPortfolioSummary(),
      ])

      // Merge portfolio data with stats from summary
      const portfoliosWithStats = portfoliosData.map(portfolio => {
        const summary = portfolioSummary.portfolios.find(p => p.id === portfolio.id)
        return {
          ...portfolio,
          project_count: summary?.project_count || 0,
          program_count: 0, // Would need program count from backend
          total_budget: summary?.total_budget || 0,
          total_actual_cost: summary?.total_actual_cost || 0,
          portfolio_code: `PORT-${portfolio.id.toString().padStart(6, '0')}`,
          priority: String(portfolio.metadata?.priority || 'Medium'),
          portfolio_type: String(portfolio.metadata?.portfolio_type || 'General'),
          manager_name: 'TBD', // Would need to fetch owner name
        }
      })

      setPortfolios(portfoliosWithStats)

      // Calculate stats
      const activePortfolios = portfoliosWithStats.filter(p => p.status === 'active').length
      const totalProjects = portfoliosWithStats.reduce((sum, p) => sum + (p.project_count || 0), 0)
      const totalPrograms = portfoliosWithStats.reduce((sum, p) => sum + (p.program_count || 0), 0)

      setStats({
        totalPortfolios: portfoliosWithStats.length,
        activePortfolios,
        totalPrograms,
        totalProjects,
      })
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      console.error('Error fetching portfolios:', error)
      toast.error(getErrorMessage(error) || 'Failed to load portfolios')
    } finally {
      setLoading(false)
    }
  }

  const handleRestore = async (id: number) => {
    try {
      await portfoliosService.restore(id)
      toast.success('Portfolio restored successfully')
      fetchPortfolios()
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      toast.error(getErrorMessage(error) || 'Failed to restore portfolio')
    }
  }

  const handleCancel = async (id: number) => {
    if (!confirm('Are you sure you want to cancel this portfolio?')) {
      return
    }
    try {
      await portfoliosService.update(id, { status: 'archived' })
      toast.success('Portfolio cancelled successfully')
      fetchPortfolios()
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      toast.error(getErrorMessage(error) || 'Failed to cancel portfolio')
    }
  }

  const uniqueTypes = Array.from(new Set(portfolios.map(p => p.portfolio_type).filter(Boolean)))
  const uniqueStatuses = Array.from(new Set(portfolios.map(p => p.status).filter(Boolean)))

  const filteredPortfolios = portfolios.filter((portfolio) => {
    const matchesSearch = 
      portfolio.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      portfolio.portfolio_code?.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesStatus = filterStatus === 'all' || portfolio.status === filterStatus
    const matchesType = filterType === 'all' || portfolio.portfolio_type === filterType
    
    return matchesSearch && matchesStatus && matchesType
  })

  const getPriorityBadge = (priority: string) => {
    const colors: Record<string, string> = {
      'Critical': 'bg-red-100 text-red-700 border-red-200',
      'High': 'bg-orange-100 text-orange-700 border-orange-200',
      'Medium': 'bg-yellow-100 text-yellow-700 border-yellow-200',
      'Low': 'bg-green-100 text-green-700 border-green-200'
    }
    return colors[priority] || 'bg-gray-100 text-gray-700 border-gray-200'
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-8">
      <div className="max-w-[1600px] mx-auto space-y-6">
        {/* Hero Section */}
        <HeroSection
          title={`${label('portfolio')} Overview`}
          description={`Manage and monitor strategic ${label('portfolio', 'plural').toLowerCase()} across the organization`}
          variant="level1"
          metrics={[
            {
              label: `Total ${label('portfolio', 'plural')}`,
              value: stats.totalPortfolios.toString(),
              icon: Briefcase,
              iconColor: 'bg-blue-50'
            },
            {
              label: `Active ${label('portfolio', 'plural')}`,
              value: stats.activePortfolios.toString(),
              icon: Activity,
              iconColor: 'bg-green-50'
            },
            { 
              label: 'Total Programs', 
              value: stats.totalPrograms.toString(), 
              icon: FolderKanban,
              iconColor: 'bg-purple-50'
            },
            { 
              label: 'Total Projects', 
              value: stats.totalProjects.toString(), 
              icon: TrendingUp,
              iconColor: 'bg-amber-50'
            },
          ]}
          actionButton={{
            label: 'Create New Portfolio',
            onClick: () => navigate('/portfolio/create'),
            icon: <Plus className="w-4 h-4" />
          }}
        />

        {/* Search and Filters */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="p-4 flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search portfolios by name or code..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
              />
            </div>

            {/* Filter by Status */}
            <div className="relative min-w-[200px]">
              <Activity className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 z-10" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full pl-10 pr-8 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white text-gray-900"
              >
                <option value="all">All Statuses</option>
                {uniqueStatuses.map(status => (
                  <option key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                  </option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>

            {/* Filter by Type */}
            <div className="relative min-w-[200px]">
              <Briefcase className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 z-10" />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full pl-10 pr-8 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white text-gray-900"
              >
                <option value="all">All Types</option>
                {uniqueTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>

            {/* Show Deleted Toggle */}
            {showDeleted ? (
              <button
                onClick={() => setShowDeleted(false)}
                className="px-4 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center gap-2"
              >
                <X className="w-4 h-4" /> Hide Deleted
              </button>
            ) : (
              <button
                onClick={() => setShowDeleted(true)}
                className="px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 text-gray-700"
              >
                <RotateCcw className="w-4 h-4" /> Show Deleted
              </button>
            )}
          </div>
        </div>

        {/* Portfolios Table */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading portfolios...</p>
              </div>
            </div>
          ) : filteredPortfolios.length === 0 ? (
            <div className="p-12 text-center">
              <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No portfolios found</h3>
              <p className="text-gray-600 mb-4">
                {searchQuery || filterStatus !== 'all' || filterType !== 'all'
                  ? 'No portfolios match your search criteria'
                  : 'No portfolios found. Create your first portfolio to get started.'}
              </p>
              {(searchQuery || filterStatus !== 'all' || filterType !== 'all') ? (
                <button
                  onClick={() => {
                    setSearchQuery('')
                    setFilterStatus('all')
                    setFilterType('all')
                  }}
                  className="px-4 py-2 text-blue-600 hover:text-blue-800"
                >
                  Clear filters
                </button>
              ) : (
                <button
                  onClick={() => navigate('/portfolio/create')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Create Portfolio
                </button>
              )}
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-700">Portfolio Code</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-700">Portfolio Name</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-700">Type</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-700">Priority</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-700">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-700">Manager</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-700">Budget</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-700">Programs/Projects</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredPortfolios.map((portfolio) => (
                  <tr 
                    key={portfolio.id} 
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/portfolio/${portfolio.id}`)}
                  >
                    <td className="px-6 py-4">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(`/portfolio/${portfolio.id}`)
                        }}
                        className="font-mono text-sm text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                      >
                        {portfolio.portfolio_code}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-gray-900">{portfolio.name}</div>
                        <div className="text-sm text-gray-500 line-clamp-1">
                          {portfolio.description || 'No description available'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-700">{portfolio.portfolio_type}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 text-xs rounded border ${getPriorityBadge(portfolio.priority || 'Medium')}`}>
                        {portfolio.priority}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge entityType="portfolio" status={portfolio.status} />
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-700">{portfolio.manager_name || 'TBD'}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-700">
                        {formatCurrency(portfolio.total_budget || 0)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3 text-sm text-gray-700">
                        <span className="flex items-center gap-1">
                          <FolderKanban className="w-4 h-4 text-gray-500" />
                          {portfolio.program_count || 0}
                        </span>
                        <span className="flex items-center gap-1">
                          <Briefcase className="w-4 h-4 text-gray-500" />
                          {portfolio.project_count || 0}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                      <div className="relative" ref={openMenuId === portfolio.id ? menuRef : null}>
                        <button
                          onClick={() => setOpenMenuId(openMenuId === portfolio.id ? null : portfolio.id)}
                          className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-500"
                        >
                          <MoreHorizontal className="w-4 h-4" />
                        </button>
                        {openMenuId === portfolio.id && (
                          <div className="absolute right-0 top-8 z-20 w-44 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                            <button
                              onClick={() => { navigate(`/portfolio/${portfolio.id}`); setOpenMenuId(null) }}
                              className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                              <Eye className="w-4 h-4 text-gray-400" /> View
                            </button>
                            <button
                              onClick={() => { navigate(`/portfolio/${portfolio.id}/edit`); setOpenMenuId(null) }}
                              className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                              <Pencil className="w-4 h-4 text-gray-400" /> Edit
                            </button>
                            {portfolio.status === 'archived' ? (
                              <button
                                onClick={() => { handleRestore(portfolio.id); setOpenMenuId(null) }}
                                className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-green-600 hover:bg-green-50 transition-colors"
                              >
                                <RotateCcw className="w-4 h-4" /> Reactivate
                              </button>
                            ) : (
                              <button
                                onClick={() => { handleCancel(portfolio.id); setOpenMenuId(null) }}
                                className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors border-t border-gray-100"
                              >
                                <X className="w-4 h-4" /> Cancel
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}

